// src/components/BulkActionsBar.tsx
// Floating action bar for multi-select bulk operations on tasks.
// Appears fixed at the bottom center when items are selected.

import { useState, useCallback, useRef, useEffect } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';

import { UserPicker } from '@/components/UserPicker';
import { statusOptions, taskPriorityOptions } from '@/features/task/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BulkActionsBarProps {
  selectedCount: number;
  onStatusChange: (status: string) => void;
  onPriorityChange: (priority: string) => void;
  onAssign: (assigneeId: string) => void;
  onDelete: () => void;
  onClearSelection: () => void;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Inline dropdown (since DropdownMenu is not in @react/components)
// ---------------------------------------------------------------------------

interface InlineDropdownProps {
  label: string;
  icon?: string;
  options: { value: string; labelKey: string }[];
  onSelect: (value: string) => void;
  disabled?: boolean;
}

function InlineDropdown({ label, options, onSelect, disabled }: InlineDropdownProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 rounded-md px-2 h-7 text-sm text-primary-foreground hover:bg-primary-foreground/10 disabled:opacity-50 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="hidden sm:inline">{label}</span>
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute bottom-full mb-1 left-0 z-50 min-w-[160px] rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-md"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              role="option"
              aria-selected={false}
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assign picker — real people, not a hardcoded "Unassigned" entry
// ---------------------------------------------------------------------------

interface AssignPickerProps {
  onAssign: (assigneeId: string) => void;
  disabled?: boolean;
}

function AssignPicker({ onAssign, disabled }: AssignPickerProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 rounded-md px-2 h-7 text-sm text-primary-foreground hover:bg-primary-foreground/10 disabled:opacity-50 transition-colors"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="hidden sm:inline">{t('task.assignee')}</span>
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('workspace.bulk.assignTo')}
          className="absolute bottom-full mb-1 left-0 z-50 w-64 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md space-y-2"
        >
          <UserPicker
            value=""
            onChange={(userId) => {
              if (!userId) return;
              onAssign(userId);
              setOpen(false);
            }}
            placeholder={t('workspace.bulk.assignTo')}
          />
          <button
            type="button"
            className="w-full rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={() => {
              onAssign('');
              setOpen(false);
            }}
          >
            {t('task.unassigned')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BulkActionsBar = observer(({
  selectedCount,
  onStatusChange,
  onPriorityChange,
  onAssign,
  onDelete,
  onClearSelection,
  isLoading = false,
}: BulkActionsBarProps) => {
  const { t } = useI18n();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDeleteClick = useCallback(() => {
    if (confirmDelete) {
      onDelete();
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  }, [confirmDelete, onDelete]);

  if (selectedCount === 0) return null;

  return (
    <div
      role="toolbar"
      aria-label={t('workspace.bulk.toolbar')}
      className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2"
    >
      <div className="flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-primary-foreground shadow-lg">
        {/* Selected count */}
        <span className="text-sm font-medium whitespace-nowrap mr-2">
          {t('workspace.bulk.selected', { count: selectedCount })}
        </span>

        <div className="h-4 w-px bg-primary-foreground/30" aria-hidden="true" />

        {/* Loading spinner */}
        {isLoading && (
          <svg
            className="h-4 w-4 animate-spin ml-1"
            fill="none"
            viewBox="0 0 24 24"
            aria-label={t('common.loading')}
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}

        {/* Status */}
        <InlineDropdown
          label={t('task.field.status')}
          options={statusOptions}
          onSelect={onStatusChange}
          disabled={isLoading}
        />

        {/* Priority */}
        <InlineDropdown
          label={t('task.field.priority')}
          options={taskPriorityOptions}
          onSelect={onPriorityChange}
          disabled={isLoading}
        />

        {/* Assign */}
        <AssignPicker onAssign={onAssign} disabled={isLoading} />

        {/* Delete */}
        {!confirmDelete ? (
          <button
            type="button"
            disabled={isLoading}
            onClick={handleDeleteClick}
            className="inline-flex items-center gap-1 rounded-md px-2 h-7 text-sm text-primary-foreground hover:bg-destructive/80 disabled:opacity-50 transition-colors"
            aria-label={t('common.delete')}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span className="hidden sm:inline">{t('common.delete')}</span>
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { onDelete(); setConfirmDelete(false); }}
              className="rounded-md px-2 h-7 text-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              {t('workspace.bulk.confirmDelete', { count: selectedCount })}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded-md px-2 h-7 text-sm text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        )}

        <div className="h-4 w-px bg-primary-foreground/30" aria-hidden="true" />

        {/* Clear selection */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          disabled={isLoading}
          className="text-primary-foreground hover:bg-primary-foreground/10 h-7 w-7 p-0"
          aria-label={t('workspace.bulk.clearSelection')}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      </div>
    </div>
  );
});

export default BulkActionsBar;
